import React, { MutableRefObject, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import ProcessBox from "./ProcessBox/ProcessBox";
import { getProductData, getProductNameBy } from "../../services/productService";
import { useLocation } from "react-router";
import LegalbirdLoader from "../ContentLoader/LegalbirdLoader";
import DocumentBox from "../PetitionSummary/DocumentBox";
import ServiceWorldCaseProductBoxes from "./ServiceWorldCase/ServiceWorldCaseProductBoxes";
import { Box, Stack } from "@mui/material";
import CalendlyAppointment from "../Calendly/CalendlyAppointment";
import CustomerFeedbackBox from "./Common/CustomerFeedbackBox";
import { useQuery } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "../../services/reactQuery/reactQueryService";
import ServiceWorldSendMessageBox from "./ServiceWorldSendMessageBox";
import useProductParams from "../../hooks/useProductParams";
import { useCustomer } from "../../provider/Customer/CustomerProvider";
import NewMessageCaseBox from "./Common/NewMessageCaseBox";
import ServiceWorldCaseDelete from "./ServiceWorldCaseDelete";
import CallBackPrototype from "./CallBackBox/CallBackPrototype";
import { gaSet } from "../../services/googleAnalyticsService";
import ProductStatus from "@/src/components/ProductStatus/ProductStatus";
import { StatusBoxDeactivatedProduct } from "@/src/components/Serviceworld/StatusBox/StatusBoxDeactivatedProduct";

const ServiceWorldCase = () => {
  const { productName, productId } = useProductParams();
  const productClassName = getProductNameBy("urlPartShort", productName);
  const productApiUri = getProductData(productClassName, "apiUri");
  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const { customer } = useCustomer();
  const location = useLocation();

  let refs: Record<string, MutableRefObject<HTMLElement | undefined>> = {
    "#documents": useRef(null!),
    "#details": useRef(null!),
    "#appointments": useRef(null!),
    "#inquiry": useRef(null!),
    "#deleteCase": useRef(null!),
  };

  useEffect(() => {
    if (location.hash && refs[location.hash] && refs[location.hash].current) {
      refs[location.hash].current?.scrollIntoView();
    }
  }, [product?.id]);

  const title = "Servicewelt Mein Fall | Legalbird";
  gaSet({ title: title });

  if (isLoading || !customer || !product) {
    return <LegalbirdLoader />;
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Stack>
        <ProductStatus product={product} />
        <Box id={"inquiry"} ref={refs["#inquiry"]} />
        <Box id={"appointments"} ref={refs["#appointments"]} />
        <CalendlyAppointment product={product} customer={customer} type={"advisoryAppointment"} />
        <CalendlyAppointment product={product} customer={customer} type={"lawyerCounselingCall"} />
        <Box id={"status"} ref={refs["#status"]} />
        {product.isDeactivatedProduct && <StatusBoxDeactivatedProduct />}
        <CallBackPrototype product={product} />
        <CustomerFeedbackBox product={product} />
        <NewMessageCaseBox product={product} />
        <ProcessBox product={product} />
        <ServiceWorldCaseProductBoxes product={product} />
        <Box id={"documents"} ref={refs["#documents"]} />
        <DocumentBox product={product} />
        <Box id={"details"} ref={refs["#details"]} />
        {product.isCustomerDataCompleted && <ServiceWorldSendMessageBox product={product} />}
        <Box id={"deleteCase"} ref={refs["#deleteCase"]} />
        <ServiceWorldCaseDelete product={product} />
      </Stack>
    </>
  );
};

export default ServiceWorldCase;
